import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Slide,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { Close as CloseIcon } from '@mui/icons-material';

const SlideTransition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  maxHeight?: string | number;
  showCloseButton?: boolean;
  /**
   * Render `actions` at the end of the scrolling content instead of pinning
   * them to the sheet footer (mobile only). Use for forms where a pinned
   * confirm button lets users submit before scrolling past required settings.
   */
  inlineActions?: boolean;
}

export const BottomSheet: React.FC<BottomSheetProps> = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  actions,
  maxHeight = '85vh',
  showCloseButton = true,
  inlineActions = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!isMobile) {
    // On desktop, render as regular centered dialog
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </Box>
            {showCloseButton && (
              <IconButton onClick={onClose} size="small" sx={{ ml: 1 }}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>{children}</DialogContent>
        {actions && <DialogActions sx={{ px: 3, pb: 2 }}>{actions}</DialogActions>}
      </Dialog>
    );
  }

  // On mobile, render as bottom sheet
  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={SlideTransition}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          m: 0,
          maxHeight,
          borderRadius: '16px 16px 0 0',
          width: '100%',
          maxWidth: '100%',
        },
      }}
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'flex-end',
        },
        '& .MuiDialog-paper': {
          width: '100%',
          margin: 0,
        },
      }}
    >
      {/* Drag Handle */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          pt: 1.5,
          pb: 0.5,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 4,
            borderRadius: 2,
            bgcolor: 'grey.300',
          }}
        />
      </Box>

      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          pb: 1.5,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.3 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {showCloseButton && (
          <IconButton onClick={onClose} size="small" sx={{ ml: 1, mt: -0.5 }}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Content */}
      <DialogContent
        sx={{
          px: 2.5,
          pt: 0,
          pb: inlineActions ? 'calc(env(safe-area-inset-bottom, 16px) + 16px)' : 2,
          overflowY: 'auto',
        }}
      >
        {children}

        {/* Inline actions scroll with the form, so the user passes every
            setting on the way to the confirm button. */}
        {actions && inlineActions && (
          <Box sx={{ mt: 2.5, pt: 2, borderTop: 1, borderColor: 'divider' }}>{actions}</Box>
        )}
      </DialogContent>

      {/* Actions */}
      {actions && !inlineActions && (
        <DialogActions
          sx={{
            px: 2.5,
            pb: 'calc(env(safe-area-inset-bottom, 16px) + 16px)',
            pt: 1.5,
            borderTop: 1,
            borderColor: 'divider',
            gap: 1,
          }}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

// Preset action buttons for common patterns
interface BottomSheetActionsProps {
  onCancel: () => void;
  onConfirm: () => void;
  cancelText?: string;
  confirmText?: string;
  confirmColor?: 'primary' | 'error' | 'success';
  confirmDisabled?: boolean;
  confirmLoading?: boolean;
  confirmIcon?: React.ReactNode;
}

export const BottomSheetActions: React.FC<BottomSheetActionsProps> = ({
  onCancel,
  onConfirm,
  cancelText = 'Cancel',
  confirmText = 'Save',
  confirmColor = 'primary',
  confirmDisabled = false,
  confirmLoading = false,
  confirmIcon,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (isMobile) {
    // Full-width stacked buttons on mobile
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
        <Button
          variant="contained"
          color={confirmColor}
          onClick={onConfirm}
          disabled={confirmDisabled || confirmLoading}
          fullWidth
          size="large"
          startIcon={confirmLoading ? <CircularProgress size={18} color="inherit" /> : confirmIcon}
          sx={{ py: 1.5, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
        >
          {confirmText}
        </Button>
        <Button
          variant="text"
          onClick={onCancel}
          fullWidth
          sx={{ py: 1, textTransform: 'none', color: 'text.secondary' }}
        >
          {cancelText}
        </Button>
      </Box>
    );
  }

  // Inline buttons on desktop
  return (
    <>
      <Button onClick={onCancel} sx={{ textTransform: 'none' }}>
        {cancelText}
      </Button>
      <Button
        variant="contained"
        color={confirmColor}
        onClick={onConfirm}
        disabled={confirmDisabled || confirmLoading}
        startIcon={confirmLoading ? <CircularProgress size={18} color="inherit" /> : confirmIcon}
        sx={{ textTransform: 'none' }}
      >
        {confirmText}
      </Button>
    </>
  );
};
